import React, { useEffect, useRef, useState } from "react";
import "../styles/experience.scss";

type IconKind = "cap" | "rocket" | "design" | "code";

type TimelineItem = {
    role: string;
    org: string;
    date: string;
    icon: IconKind;
    body: string;
};

// oldest → newest here; rendered reversed so the newest sits on top
const ITEMS: TimelineItem[] = [
    {
        role: "Student",
        org: "Kansas City Art Institute",
        date: "Aug 2018 — Dec 2020",
        icon: "cap",
        body: "Studied design, color theory, and composition. It's the visual grounding behind my UI work.",
    },
    {
        role: "Student",
        org: "The LaunchCode Foundation",
        date: "Jun 2021 — Dec 2021",
        icon: "rocket",
        body: "Immersive web-development program (Java, JavaScript, HTML/CSS, Angular, SQL), capped by a self-directed final project and an apprenticeship placement.",
    },
    {
        role: "Graphic Designer",
        org: "Corporate Copyprint",
        date: "Jun 2021 — Mar 2022",
        icon: "design",
        body: "Designed advertising materials and product concepts across the Adobe Creative Suite, lifting client engagement and driving repeat business.",
    },
    {
        role: "Software Engineer",
        org: "Viagio Technologies",
        date: "Apr 2022 — Nov 2023",
        icon: "code",
        body: "Shipped features and streamlined processes across educational and business software, ramping quickly on new stacks and hardening stability through testing and close collaboration.",
    },
    {
        role: "Software Engineer",
        org: "Weitzman",
        date: "Sep 2024 — Present",
        icon: "code",
        body: "Builds internal applications end to end, from discovery and UI design through development, testing, and rollout, and improves workflows with automation and AI-assisted tooling.",
    },
];

const ICON_PATHS: Record<IconKind, React.ReactNode> = {
    cap: (
        <>
            <path d="M22 10 12 5 2 10l10 5 10-5Z" />
            <path d="M6 12v5c3 2.5 9 2.5 12 0v-5" />
        </>
    ),
    rocket: (
        <>
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
            <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </>
    ),
    design: (
        <>
            <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 10 10c0 3.31-2.69 4-4 4h-2a2 2 0 0 0-2 2 2 2 0 0 1-2 2Z" />
            <circle cx="8.5" cy="9.5" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="12" cy="7" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="15.5" cy="9.5" r="1.1" fill="currentColor" stroke="none" />
        </>
    ),
    code: (
        <>
            <path d="m7 11 2-2-2-2" />
            <path d="M11 13h4" />
            <rect x="3" y="3" width="18" height="18" rx="3" />
        </>
    ),
};

const NodeIcon: React.FC<{ kind: IconKind }> = ({ kind }) => (
    <svg
        width="27"
        height="27"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {ICON_PATHS[kind]}
    </svg>
);

// ⬡ stop 4: the timeline. hexagons all the way down (see the fun fact at the bottom).
const Experience: React.FC = () => {
    // wait until the timeline is on screen to animate, or it all plays where no one sees it
    const rootRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry], obs) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <div id="experience-section" className="section">
            <div className="experience-waves top"></div>
            <div className="description-wrapper">
                <header className="experience-header">Experience</header>
                <p className="experience-description">
                    I think a lot about the "curb-cut effect"; an idea inspired by replacing 6 inches of a concrete curb into a slope on the sidewalks. A redesign that makes it easier for people with a disability to get around, and in turn makes it easier for everyone.
                    <br />
                    <br />
                    I worked at a tech agency for two years, which required getting up to speed on a new tech stack with every project. With these opportunities, I hope to create a lot of curb cuts in tech and make it easier for everyone to connect.
                </p>
            </div>
            <div className="experience-waves bottom"></div>
            <div ref={rootRef} className={`xp${visible ? " is-visible" : ""}`}>
                <div className="xp-inner">
                    <div className="xp-kicker">
                        <span className="xp-kicker-dash" />
                        <span className="xp-kicker-label">The path so far</span>
                    </div>
                    <h2 className="xp-title">Design roots, engineering craft.</h2>

                    <div className="xp-track">
                        <div className="xp-line" />

                        {/* résumé up top, then roles newest → oldest */}
                        <div className="xp-item xp-resume" style={{ animationDelay: "0s" }}>
                            <div className="xp-node xp-node--teal">
                                <svg
                                    width="26"
                                    height="26"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                    <path d="M14 2v6h6" />
                                    <path d="M9 13h6" />
                                    <path d="M9 17h6" />
                                </svg>
                            </div>
                            <a className="xp-resume-btn" href="/resume.pdf" download="ZoeDonovan.pdf">
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <path d="M7 10l5 5 5-5" />
                                    <path d="M12 15V3" />
                                </svg>
                                Download résumé
                            </a>
                        </div>

                        {ITEMS.slice().reverse().map((it, i) => (
                            <div
                                key={it.org + it.date}
                                className="xp-item"
                                style={{ animationDelay: `${(i + 1) * 0.1}s` }}
                            >
                                <div className="xp-node">
                                    <NodeIcon kind={it.icon} />
                                </div>
                                <div className="xp-card">
                                    <div className="xp-card-date">{it.date}</div>
                                    <h3 className="xp-card-role">{it.role}</h3>
                                    <div className="xp-card-org">{it.org}</div>
                                    <p className="xp-card-body">{it.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="hex-fact">
                        <svg
                            className="hex-fact-icon"
                            width="30"
                            height="30"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#04919c"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M12 2 21 7.2v9.6L12 22l-9-5.2V7.2Z" />
                        </svg>
                        <p>
                            <strong>Fun fact:</strong> the hexagon is nature's strongest shape, and the most
                            efficient way to fill a space with the least material. That's why bees swear by
                            it, and why this site is full of them.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Experience;
